import React, { useEffect, useState, useRef, useMemo } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Modal, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import SectionLoader from "../../../components/SectionLoader";
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import axios from "axios";
import JoditEditor from "jodit-react";
import { error, success } from "../../../notify";

// Helper function to truncate HTML/Markdown text safely
const truncateText = (text, maxLength = 100) => {
    if (!text) return "N/A";

    // Remove HTML tags for length calculation and display
    const plainText = text.replace(/<[^>]*>/g, '');

    if (plainText.length <= maxLength) {
        return plainText;
    }

    return `${plainText.substring(0, maxLength)}...`;
};

// Define the reaction icons - OUTLINE VERSIONS (not filled)
const ThumbUpIcon = ({ className, style }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        style={style}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
    </svg>
);

const ThumbDownIcon = ({ className, style }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        style={style}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h3a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-3" />
    </svg>
);

const NeutralIcon = ({ className, style }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        style={style}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <circle cx="12" cy="12" r="10" />
        <line x1="8" y1="12" x2="16" y2="12" />
    </svg>
);

const EditIcon = () => (
    <svg width="16" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M13.7476 20.4428H21.0002" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path fillRule="evenodd" clipRule="evenodd" d="M12.78 3.79479C13.5557 2.86779 14.95 2.73186 15.8962 3.49173C15.9485 3.53296 17.6295 4.83879 17.6295 4.83879C18.669 5.46719 18.992 6.80311 18.3494 7.82259C18.3153 7.87718 8.81195 19.7645 8.81195 19.7645C8.49578 20.1589 8.01583 20.3918 7.50291 20.3973L3.86353 20.443L3.04353 16.9723C2.92866 16.4843 3.04353 15.9718 3.3597 15.5773L12.78 3.79479Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M11.021 6.00098L16.4732 10.1881" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ViewIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);

const RefreshIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5a6acf" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 2v6h6"></path>
        <path d="M21 12a9 9 0 0 0-15-6.7L3 8"></path>
        <path d="M21 22v-6h-6"></path>
        <path d="M3 12a9 9 0 0 0 15 6.7L21 16"></path>
    </svg>
);

const BackIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 18 9 12 15 6"></polyline>
        <line x1="5" y1="6" x2="5" y2="18"></line>
    </svg>
);

const SearchIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.3-4.3" />
    </svg>
);

const ClearIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

const ExportIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#5a6acf"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
        <polyline points="7 10 12 15 17 10"></polyline>
        <line x1="12" y1="15" x2="12" y2="3"></line>
    </svg>
);

const ChatHistory = () => {
    const [chatHistory, setChatHistory] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showBotResponseModal, setShowBotResponseModal] = useState(false);
    const [botResponse, setBotResponse] = useState("");
    const [isFetchingBotResponse, setIsFetchingBotResponse] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [editResponse, setEditResponse] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [itemsPerPage, setItemsPerPage] = useState(() => {
        return sessionStorage.getItem("current_page_limit")
            ? parseInt(sessionStorage.getItem("current_page_limit"), 10)
            : 50;
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [currentRangeStart, setCurrentRangeStart] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [reactionFilter, setReactionFilter] = useState("all");
    const [dateFilter, setDateFilter] = useState("all");
    const [searchInputValue, setSearchInputValue] = useState("");
    const [showCustomized, setShowCustomized] = useState(false);
    const [showClearButton, setShowClearButton] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportReaction, setExportReaction] = useState("all");
    const [exportDateFilter, setExportDateFilter] = useState("Last 1 year");
    const [exportFormat, setExportFormat] = useState("xlsx");
    const [isExporting, setIsExporting] = useState(false);

    const offset = useRef(0);
    const isInitialPageLimitSet = useRef(false);
    const botResponseButtonRef = useRef(null);
    const pagesToShow = 10;
    const getQAapi = `${process.env.REACT_APP_SERVER_URL}/api/website/queries`;
    const getBotResponseApi = `${process.env.REACT_APP_SERVER_URL}/api/website/customization/get_bot_response`;
    const updateResponseApi = `${process.env.REACT_APP_SERVER_URL}/api/website/queries/custom_response`;
    const deleteResponseApi = `${process.env.REACT_APP_SERVER_URL}/api/website/queries/custom_response`;
    const location = useLocation();
    const { siteId, siteName } = location.state || {};
    const userRole = sessionStorage.getItem("userRole");
    const setPageLimitURL = `${process.env.REACT_APP_SERVER_URL}/api/set-page-limit/`;
    const token = sessionStorage.getItem("revizeTrainingToken");

    const navigate = useNavigate();

    // Jodit editor configuration
    const joditConfig = useMemo(
        () => ({
            readonly: false,
            buttonsMD: ["bold", "italic", "|", "ul", "ol", "link", "image"],
            buttonsSM: ["bold", "italic", "|", "ul", "ol", "link", "image"],
            buttonsXS: ["bold", "italic", "|", "ul", "ol", "link", "image"],
            statusbar: false,
            link: {
                openInNewTabCheckbox: true,
                noFollowCheckbox: false,
            },
        }),
        []
    );

    // Search functionality

    const handleSearch = async () => {
        if (searchInputValue.trim() !== "") {
            setShowClearButton(true);
        }
        setCurrentPage(1);
        setCurrentRangeStart(1);
        const newOffset = 0;
        offset.current = newOffset;
        await fetchChatHistory(newOffset, searchInputValue);
    };

    const handleClearSearch = async () => {
        setSearchInputValue("");
        setShowClearButton(false);
        setCurrentPage(1);
        setCurrentRangeStart(1);
        const newOffset = 0;
        offset.current = newOffset;
        await fetchChatHistory(newOffset, "");
    };

    // Refresh functionality
    const handleRefresh = async () => {
        const newOffset = (currentPage - 1) * itemsPerPage;
        offset.current = newOffset;
        await fetchChatHistory(newOffset, searchInputValue);
        success("Chat history refreshed successfully.", true);
    };

    useEffect(() => {
        const updatePageLimit = async () => {
            const headers = {
                "accept": "application/json",
                "Authorization": "Bearer " + token
            };
            const loggedInUserId = sessionStorage.getItem("loggedInUserId");

            if (!isInitialPageLimitSet.current) {
                isInitialPageLimitSet.current = true;
                return;
            }
            if (itemsPerPage != null) {
                try {
                    await axios.post(setPageLimitURL,
                        { page_limit: itemsPerPage },
                        {
                            headers: headers,
                            params: { user_id: loggedInUserId }
                        }
                    );
                    sessionStorage.setItem('current_page_limit', itemsPerPage);
                    sessionStorage.setItem('previous_page_limit', itemsPerPage);
                } catch (setLimitErr) {
                    console.error('Error setting page limit:', setLimitErr);
                }
            }
        };
        updatePageLimit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemsPerPage]);

    // Fetch chat history with offset, limit, show_customized_response, reaction filter, and date filter
    const fetchChatHistory = async (offset, searchText = "") => {
        setIsLoading(true);
        try {
            let url = `${getQAapi}/${siteId}?offset=${offset}&limit=${itemsPerPage}&show_customized_response=${showCustomized}`;

            // Add search filter if provided
            if (searchText.trim() !== "") {
                url += `&search_text=${encodeURIComponent(searchText.trim())}`;
            }

            // Add reaction filter if not "all"
            if (reactionFilter !== "all") {
                url += `&reaction=${reactionFilter}`;
            }

            // Add date filter if not "all"
            if (dateFilter !== "all") {
                url += `&date_filter=${encodeURIComponent(dateFilter)}`;
            }

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            const chats = data.queries || [];
            setChatHistory(chats);
            if (data.total_count !== undefined) {
                setTotalCount(data.total_count);
                setTotalPages(Math.ceil(data.total_count / itemsPerPage));
            }
        } catch (error) {
            console.error("Error fetching chat history:", error);
            setChatHistory([]);
            setTotalCount(0);
            setTotalPages(1);
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch bot response
    const fetchBotResponse = async () => {
        if (!siteId || !selectedChat?.query.trim()) {
            error("Website ID or query is missing", true);
            return;
        }

        try {
            setIsFetchingBotResponse(true);
            const response = await axios.get(
                `${getBotResponseApi}/${siteId}`,
                {
                    headers: {
                        "accept": "application/json",
                        "Authorization": "Bearer " + token
                    },
                    params: {
                        query: selectedChat.query
                    }
                }
            );
            setBotResponse(response.data || "No response received");
            setShowBotResponseModal(true);
        } catch (err) {
            console.error("Error fetching bot response:", err);
            error(err.response?.data?.detail || "Failed to fetch bot response", true);
        } finally {
            setIsFetchingBotResponse(false);
        }
    };

    // Update chat history when page, checkbox, reaction filter, or date filter changes
    useEffect(() => {
        const newOffset = (currentPage - 1) * itemsPerPage;
        offset.current = newOffset;
        fetchChatHistory(newOffset, searchInputValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, itemsPerPage, showCustomized, reactionFilter, dateFilter]);

    // Position the bot response modal
    useEffect(() => {
        const updateModalPosition = () => {
            if (showBotResponseModal && botResponseButtonRef.current) {
                const buttonRect = botResponseButtonRef.current.getBoundingClientRect();
                const modal = document.querySelector('.bot-response-modal');
                if (modal) {
                    modal.style.position = 'fixed';
                    modal.style.top = `${buttonRect.top - 45}px`;
                    modal.style.left = `${buttonRect.right + 10}px`;
                    modal.style.zIndex = '1050';

                    const modalRect = modal.getBoundingClientRect();
                    const viewportWidth = window.innerWidth;
                    const viewportHeight = window.innerHeight;

                    if (modalRect.right > viewportWidth) {
                        modal.style.left = `${buttonRect.left - modalRect.width - 10}px`;
                    }

                    if (modalRect.bottom > viewportHeight) {
                        modal.style.top = `${viewportHeight - modalRect.height - 10}px`;
                    }

                    if (modalRect.top < 0) {
                        modal.style.top = '10px';
                    }
                }
            }
        };

        updateModalPosition();

        if (showBotResponseModal) {
            window.addEventListener('scroll', updateModalPosition);
            window.addEventListener('resize', updateModalPosition);

            const modal = document.querySelector('.bot-response-modal');
            let resizeObserver;

            if (modal) {
                resizeObserver = new ResizeObserver(updateModalPosition);
                resizeObserver.observe(modal);
            }

            return () => {
                window.removeEventListener('scroll', updateModalPosition);
                window.removeEventListener('resize', updateModalPosition);
                if (resizeObserver) {
                    resizeObserver.disconnect();
                }
            };
        }
    }, [showBotResponseModal]);

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return "N/A";

        const plainTimestamp = timestamp
            .replace("T", " ")       // remove the T separator
            .substring(0, 19);        // strip the offset (+05:30 / -05:00 / etc.)

        const [datePart, timePart] = plainTimestamp.split(" ");
        const [year, month, day] = datePart.split("-");
        const [hour, minute] = timePart.split(":");

        const formattedDate = `${month}/${day}/${year.slice(2)}`; // MM/DD/YY
        const formattedTime = `${hour}:${minute}`;                // HH:MM

        return `${formattedDate}, ${formattedTime}`;
    };

    const getEffectiveResponse = (chat) => {
        return chat.customized_response || chat.response;
    };

    const openViewChatModal = (chat) => {
        setSelectedChat(chat);
        setShowViewModal(true);
    };

    const openEditChatModal = (chat, event) => {
        event.stopPropagation();
        setSelectedChat(chat);
        setEditResponse(chat.customized_response || chat.response);
        setShowEditModal(true);
    };

    const handleCloseViewModal = () => {
        setShowViewModal(false);
        setSelectedChat(null);
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setSelectedChat(null);
        setEditResponse("");
        setShowBotResponseModal(false);
        setBotResponse("");
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
    };

    const handleCloseBotResponseModal = () => {
        setShowBotResponseModal(false);
        setBotResponse("");
    };

    const handleCopyResponse = async () => {
        try {
            await navigator.clipboard.writeText(botResponse);
            success("Response copied to clipboard", true);
        } catch (err) {
            console.error("Error copying response:", err);
            error("Failed to copy response", true);
        }
    };

    const handleSaveChanges = async () => {
        if (!selectedChat) return;

        try {
            setIsSaving(true);
            if (!siteId) {
                error("Website ID is missing", true);
                return;
            }

            await axios.post(
                updateResponseApi,
                {
                    website_id: siteId,
                    query_response_id: selectedChat.chat_history_id,
                    query: selectedChat.query,
                    response: editResponse
                },
                {
                    headers: {
                        "accept": "application/json",
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + token
                    }
                }
            );

            setChatHistory(prev =>
                prev.map(item =>
                    item.chat_history_id === selectedChat.chat_history_id
                        ? { ...item, customized_response: editResponse }
                        : item
                )
            );

            success("Customized response is captured", true);
            handleCloseEditModal();
        } catch (err) {
            console.error("Error updating response:", err);
            error(err.response?.data?.message || "Failed to update response", true);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteResponse = async () => {
        if (!selectedChat || !siteId) {
            error("Website ID or chat selection is missing", true);
            return;
        }
        if (!selectedChat.customized_response) {
            error("No customized response to delete", true);
            return;
        }

        const chatId = selectedChat.chat_history_id;
        try {
            setIsDeleting(true);
            setChatHistory((prev) =>
                prev.map((item) =>
                    item.chat_history_id === chatId ? { ...item, customized_response: null } : item
                )
            );

            await axios.delete(deleteResponseApi, {
                headers: {
                    accept: "application/json",
                    Authorization: "Bearer " + token,
                },
                params: {
                    website_id: siteId,
                    query_response_id: chatId,
                },
            });

            success("Customization removed successfully", true);
            handleCloseEditModal();
            handleCloseDeleteModal();
        } catch (err) {
            setChatHistory((prev) =>
                prev.map((item) =>
                    item.chat_history_id === chatId
                        ? { ...item, customized_response: selectedChat.customized_response }
                        : item
                )
            );
            console.error("Error deleting response:", err);
            const errorMessage =
                err.response?.status === 404
                    ? "Customized response not found"
                    : err.response?.data?.message || "Failed to delete response";
            error(errorMessage, true);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleShowDeleteModal = (event) => {
        event.stopPropagation();
        setShowEditModal(false);
        setShowDeleteModal(true);
    };

    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
        const newOffset = (pageNumber - 1) * itemsPerPage;
        offset.current = newOffset;
    };

    const handleNextRange = () => {
        if (currentRangeStart + pagesToShow <= totalPages) {
            setCurrentRangeStart(currentRangeStart + pagesToShow);
        }
    };

    const handlePrevRange = () => {
        if (currentRangeStart - pagesToShow > 0) {
            setCurrentRangeStart(currentRangeStart - pagesToShow);
        }
    };

    const visiblePageNumbers = Array.from(
        { length: Math.min(pagesToShow, totalPages - currentRangeStart + 1) },
        (_, index) => currentRangeStart + index
    );

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(parseInt(e.target.value, 10));
        setCurrentPage(1);
        setCurrentRangeStart(1);
    };

    const handleCheckboxChange = () => {
        setShowCustomized(prev => !prev);
        setCurrentPage(1);
        setCurrentRangeStart(1);
    };

    const handleReactionFilterChange = (e) => {
        const filterValue = e.target.value;
        console.log("Reaction filter changed to:", filterValue);
        setReactionFilter(filterValue);
        setCurrentPage(1);
        setCurrentRangeStart(1);
    };

    const handleDateFilterChange = (e) => {
        const filterValue = e.target.value;
        console.log("Date filter changed to:", filterValue);
        setDateFilter(filterValue);
        setCurrentPage(1);
        setCurrentRangeStart(1);
    };

    const handleExport = async () => {
        if (!siteId) {
            error("Website ID is missing", true);
            return;
        }

        setIsExporting(true);

        try {
            // Build query parameters
            const params = new URLSearchParams({
                reaction: exportReaction,
                date_filter: exportDateFilter,
                format: exportFormat
            });

            const url = `${process.env.REACT_APP_SERVER_URL}/api/website/queries/export_chats/${siteId}?${params}`;

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Export failed');
            }

            // Get filename from Content-Disposition or create one
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = `chat_history.${exportFormat}`;
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?(.+)"?/);
                if (match) filename = match[1];
            }

            // Trigger download
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);

            success("Export completed successfully", true);
            setShowExportModal(false);
        } catch (err) {
            console.error("Export error:", err);
            error(err.message || "Failed to export chat history", true);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div style={{
            fontFamily: '"Inter", sans-serif',
            background: '#f8f9fd',
            margin: 0,
            padding: '20px',
            minHeight: '100vh',
            width: '100%',
            boxSizing: 'border-box'
        }}>
            <div style={{
                width: '100%',
                maxWidth: '100%',
                overflowX: 'auto',
                boxSizing: 'border-box'
            }}>
                <div style={{
                    background: '#fff',
                    borderRadius: '16px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    padding: '20px',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                }}>
                    {/* Header Section */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '15px',
                        gap: '12px'
                    }}>
                        <h2 style={{ fontSize: '20px', margin: 0, fontWeight: 600 }}>
                            Chat History - {siteName}
                        </h2>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                            <div style={{ position: 'relative', width: '350px', minWidth: '250px' }}>
                                <input
                                    type="text"
                                    placeholder="Search responses..."
                                    value={searchInputValue}
                                    onChange={(e) => {
                                        setSearchInputValue(e.target.value);
                                        if (e.target.value.trim() === "") {
                                            setShowClearButton(false);
                                            handleClearSearch();
                                        }
                                    }}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSearch();
                                        }
                                    }}
                                    style={{
                                        width: '100%',
                                        padding: '8px 68px 8px 12px',
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        fontSize: '14px'
                                    }}
                                />
                                <div style={{
                                    position: 'absolute',
                                    right: '4px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    display: 'flex',
                                    gap: '4px',
                                    alignItems: 'center'
                                }}>
                                    {showClearButton && (
                                        <Button
                                            variant="outline-secondary"
                                            onClick={handleClearSearch}
                                            style={{
                                                background: '#5a6acf',
                                                borderRadius: '8px',
                                                width: '28px',
                                                height: '28px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                padding: '0',
                                                cursor: 'pointer',
                                                color: '#5a6acf',
                                                transition: 'all 0.2s ease-in-out',
                                                border: '1px solid #ddd',
                                                backgroundColor: 'white',
                                                boxShadow: '0 1px 1px rgba(0, 0, 0, 0.15)',
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = '#5a6acf';
                                                e.currentTarget.style.color = 'white';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'white';
                                                e.currentTarget.style.color = '#5a6acf';
                                            }}
                                        >
                                            <ClearIcon />
                                        </Button>
                                    )}
                                    <Button
                                        variant="primary"
                                        onClick={handleSearch}
                                        style={{
                                            background: '#5a6acf',
                                            borderRadius: '8px',
                                            width: '28px',
                                            height: '28px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: '0',
                                            cursor: 'pointer',
                                            color: '#5a6acf',
                                            transition: 'all 0.2s ease-in-out',
                                            border: '1px solid #ddd',
                                            backgroundColor: 'white',
                                            boxShadow: '0 1px 1px rgba(0, 0, 0, 0.15)',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#5a6acf';
                                            e.currentTarget.style.color = 'white';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'white';
                                            e.currentTarget.style.color = '#5a6acf';
                                        }}
                                    >
                                        <SearchIcon />
                                    </Button>
                                </div>
                            </div>
                            <Form.Check
                                type="checkbox"
                                id="showCustomized"
                                label="Show customized records"
                                checked={showCustomized}
                                onChange={handleCheckboxChange}
                                className="me-0"
                                style={{ margin: 0, whiteSpace: 'nowrap' }}
                            />
                            <Form.Select
                                value={reactionFilter}
                                onChange={handleReactionFilterChange}
                                style={{ width: 'auto', marginRight: '10px', minWidth: '150px' }}
                            >
                                <option value="all">All Reactions</option>
                                <option value="1">Positive</option>
                                <option value="-1">Negative</option>
                                <option value="0">Neutral</option>
                            </Form.Select>
                            <Form.Select
                                value={dateFilter}
                                onChange={handleDateFilterChange}
                                style={{ width: 'auto', marginRight: '10px', minWidth: '150px' }}
                            >
                                <option value="Today">Today</option>
                                <option value="Last 7 days">Last 7 days</option>
                                <option value="Last 30 days">Last 30 days</option>
                                <option value="Last 1 year">Last 1 year</option>
                                <option value="all">All Dates</option>
                            </Form.Select>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <OverlayTrigger placement="top" overlay={<Tooltip>Export Chat History</Tooltip>}>
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => setShowExportModal(true)}
                                    style={{
                                        background: 'white',
                                        border: '1px solid #ddd',
                                        borderRadius: '50%',
                                        width: '40px',
                                        height: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0',
                                        fontSize: '14px',
                                        color: '#5a6acf',
                                        cursor: 'pointer',
                                        flexShrink: 0
                                    }}
                                >
                                    <ExportIcon />
                                </Button>
                            </OverlayTrigger>

                            <OverlayTrigger placement="top" overlay={<Tooltip>Refresh Data</Tooltip>}>
                                <Button
                                    variant="outline-secondary"
                                    onClick={handleRefresh}
                                    style={{
                                        background: 'white',
                                        border: '1px solid #ddd',
                                        borderRadius: '50%',
                                        width: '40px',
                                        height: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0',
                                        fontSize: '14px',
                                        color: '#5a6acf',
                                        cursor: 'pointer',
                                        flexShrink: 0
                                    }}
                                >
                                    <RefreshIcon />
                                </Button>
                            </OverlayTrigger>

                            <OverlayTrigger placement="top" overlay={<Tooltip>Back to Sites</Tooltip>}>
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => navigate(-1)}
                                    style={{
                                        background: 'white',
                                        border: '1px solid #ddd',
                                        borderRadius: '50%',
                                        width: '40px',
                                        height: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0',
                                        fontSize: '14px',
                                        color: '#5a6acf',
                                        cursor: 'pointer',
                                        flexShrink: 0
                                    }}
                                >
                                    <BackIcon />
                                </Button>
                            </OverlayTrigger>
                        </div>
                    </div>

                    {/* Table Section - Wrapped in scrollable container */}
                    <div style={{ width: '100%', overflowX: 'auto' }}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            minWidth: '800px'
                        }}>
                            <thead>
                                <tr style={{ textAlign: 'left', fontWeight: 600, fontSize: '14px', color: '#555' }}>
                                    <th style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', width: '5%', textAlign: 'center' }}>Reaction</th>
                                    <th style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', width: '25%' }}>Prompt</th>
                                    <th style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', width: '25%' }}>Response</th>
                                    <th style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', width: '15%', textAlign: 'center' }}>Timestamp</th>
                                    {userRole === "ADMIN" && <th style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', width: '15%', textAlign: 'center' }}>Response Time</th>}
                                    <th style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', width: '5%', textAlign: 'center' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan={userRole === "ADMIN" ? 6 : 5} style={{ textAlign: 'center', padding: '14px 10px' }}>
                                            <SectionLoader label="Loading chat history..." className="revize-section-loader--table" />
                                        </td>
                                    </tr>
                                ) : chatHistory.length === 0 ? (
                                    <tr>
                                        <td colSpan={userRole === "ADMIN" ? 6 : 5} style={{ textAlign: 'center', padding: '14px 10px', color: '#888' }}>
                                            {searchInputValue ? 'No responses found matching your search.' : 'No chat history available yet.'}
                                        </td>
                                    </tr>
                                ) : (
                                chatHistory.map((chat, index) => (
                                    <tr key={index} style={{ cursor: 'pointer' }}>
                                        <td
                                            style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}
                                            onClick={() => openViewChatModal(chat)}
                                        >
                                            {chat.reaction === 1 && (
                                                <ThumbUpIcon className="text-secondary" style={{ width: '1.2rem', height: '1.2rem' }} />
                                            )}
                                            {chat.reaction === -1 && (
                                                <ThumbDownIcon className="text-secondary" style={{ width: '1.2rem', height: '1.2rem' }} />
                                            )}
                                            {chat.reaction === 0 && (
                                                <NeutralIcon className="text-secondary" style={{ width: '1.2rem', height: '1.2rem' }} />
                                            )}
                                        </td>
                                        <td
                                            style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0' }}
                                            onClick={() => openViewChatModal(chat)}
                                        >
                                            {truncateText(chat.query, 50)}
                                        </td>
                                        <td
                                            style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0' }}
                                            onClick={() => openViewChatModal(chat)}
                                        >
                                            <div style={{ display: 'inline-block', textAlign: 'left' }}>
                                                {truncateText(getEffectiveResponse(chat), 50)}
                                            </div>
                                        </td>
                                        <td
                                            style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}
                                            onClick={() => openViewChatModal(chat)}
                                        >
                                            {formatTimestamp(chat.time_stamp)}
                                        </td>
                                        {userRole === 'ADMIN' && (
                                            <td
                                                style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}
                                                onClick={() => openViewChatModal(chat)}
                                            >
                                                {chat.response_time?.toFixed(1)}
                                            </td>
                                        )}
                                        <td style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                                            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', alignItems: 'center', color: '#555' }}>
                                                <OverlayTrigger placement="top" overlay={<Tooltip>View Details</Tooltip>}>
                                                    <span
                                                        onClick={() => openViewChatModal(chat)}
                                                        style={{
                                                            cursor: 'pointer',
                                                            display: 'inline-flex',
                                                            color: '#555',
                                                            transition: 'color 0.3s ease',
                                                        }}
                                                        onMouseOver={(e) => (e.currentTarget.style.color = '#5a6acf')}
                                                        onMouseOut={(e) => (e.currentTarget.style.color = '#555')}
                                                    >
                                                        <ViewIcon />
                                                    </span>
                                                </OverlayTrigger>

                                                <OverlayTrigger placement="top" overlay={<Tooltip>Customize Response</Tooltip>}>
                                                    <span
                                                        onClick={(e) => openEditChatModal(chat, e)}
                                                        style={{
                                                            cursor: 'pointer',
                                                            display: 'inline-flex',
                                                            color: chat.customized_response ? '#28a745' : '#555',
                                                            transition: 'color 0.3s ease',
                                                        }}
                                                        onMouseOver={(e) =>
                                                            (e.currentTarget.style.color = chat.customized_response ? '#218838' : '#5a6acf')
                                                        }
                                                        onMouseOut={(e) =>
                                                            (e.currentTarget.style.color = chat.customized_response ? '#28a745' : '#555')
                                                        }
                                                    >
                                                        <EditIcon />
                                                    </span>
                                                </OverlayTrigger>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Section - Fixed condition */}
                    {!isLoading && chatHistory.length > 0 && (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginTop: '15px',
                            fontSize: '14px',
                            color: '#888',
                            flexWrap: 'wrap',
                            gap: '10px'
                        }}>
                            <div style={{ flex: '1 1 200px', textAlign: 'left' }}>
                                <span>Showing data {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} entries</span>
                            </div>

                            <div style={{
                                flex: '1 1 auto',
                                display: 'flex',
                                justifyContent: 'center',
                                gap: '6px',
                                alignItems: 'center',
                                flexWrap: 'wrap'
                            }}>
                                <button
                                    onClick={handlePrevRange}
                                    disabled={currentRangeStart === 1}
                                    style={{
                                        padding: '4px 8px',
                                        border: '1px solid #ddd',
                                        borderRadius: '6px',
                                        background: '#fff',
                                        cursor: currentRangeStart === 1 ? 'not-allowed' : 'pointer',
                                        fontSize: '13px',
                                        opacity: currentRangeStart === 1 ? 0.5 : 1
                                    }}
                                >
                                    &lt;
                                </button>

                                {visiblePageNumbers.map((number) => (
                                    <button
                                        key={number}
                                        onClick={() => paginate(number)}
                                        style={{
                                            padding: '4px 12px',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            background: currentPage === number ? '#5A6ACF' : '#fff',
                                            color: currentPage === number ? '#fff' : 'inherit',
                                            cursor: 'pointer',
                                            fontSize: '13px'
                                        }}
                                    >
                                        {number}
                                    </button>
                                ))}

                                {currentRangeStart + pagesToShow <= totalPages && (
                                    <span>...</span>
                                )}

                                <button
                                    onClick={handleNextRange}
                                    disabled={currentRangeStart + pagesToShow > totalPages}
                                    style={{
                                        padding: '4px 8px',
                                        border: '1px solid #ddd',
                                        borderRadius: '6px',
                                        background: '#fff',
                                        cursor: currentRangeStart + pagesToShow > totalPages ? 'not-allowed' : 'pointer',
                                        fontSize: '13px',
                                        opacity: currentRangeStart + pagesToShow > totalPages ? 0.5 : 1
                                    }}
                                >
                                    &gt;
                                </button>
                            </div>

                            <div style={{ flex: '1 1 200px', textAlign: 'right' }}>
                                <label htmlFor="itemsPerPage" style={{ marginRight: '10px' }}>Items per page:</label>
                                <select
                                    id="itemsPerPage"
                                    value={itemsPerPage}
                                    onChange={handleItemsPerPageChange}
                                    style={{
                                        padding: '6px 12px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        height: '32px',
                                        width: '80px',
                                        fontSize: '13px',
                                        appearance: 'menulist'
                                    }}
                                >
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                    <option value={150}>150</option>
                                    <option value={200}>200</option>
                                    <option value={500}>500</option>
                                    <option value={1000}>1000</option>
                                </select>
                            </div>
                        </div>
                    )}
                </div>

                {/* View Modal */}
                {selectedChat && (
                    <Modal show={showViewModal} onHide={handleCloseViewModal} size="xl">
                        <Modal.Header closeButton>
                            <Modal.Title>Chat Details</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <div className="d-flex justify-content-between" style={{ gap: '20px', marginBottom: '20px' }}>
                                <div style={{ flex: 1 }}>
                                    <h5>Timestamp:</h5>
                                    <pre style={{
                                        backgroundColor: '#f4f4f4',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        maxHeight: '300px',
                                        overflowY: 'auto',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word'
                                    }}>
                                        {formatTimestamp(selectedChat.time_stamp)}
                                    </pre>
                                </div>
                                {userRole === 'ADMIN' && selectedChat && (
                                    <div style={{ flex: 1 }}>
                                        <h5>Response Time (In seconds):</h5>
                                        <pre style={{
                                            backgroundColor: '#f4f4f4',
                                            padding: '10px',
                                            borderRadius: '5px',
                                            maxHeight: '300px',
                                            overflowY: 'auto',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word'
                                        }}>
                                            {selectedChat.response_time ? selectedChat.response_time.toFixed(1) : 'N/A'}
                                        </pre>
                                    </div>
                                )}
                            </div>
                            <div className="mt-3">
                                <h5>Prompt:</h5>
                                <pre style={{
                                    backgroundColor: '#f4f4f4',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    maxHeight: '300px',
                                    overflowY: 'auto',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                }}>
                                    {selectedChat.query}
                                </pre>
                            </div>
                            <div className="mt-3">
                                <h5>Response:</h5>
                                <pre style={{
                                    backgroundColor: '#f4f4f4',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    maxHeight: '300px',
                                    overflowY: 'auto',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                }}>
                                    <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                                        {getEffectiveResponse(selectedChat)}
                                    </ReactMarkdown>
                                </pre>
                            </div>
                            {selectedChat.customized_response && (
                                <div className="mt-3">
                                    <h5>Original Response:</h5>
                                    <pre style={{
                                        backgroundColor: '#f4f4f4',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        maxHeight: '300px',
                                        overflowY: 'auto',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word'
                                    }}>
                                        <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                                            {selectedChat.response}
                                        </ReactMarkdown>
                                    </pre>
                                </div>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="secondary"
                                onClick={handleCloseViewModal}
                                style={{ border: '1px solid #ddd', minWidth: '100px' }}
                            >
                                Close
                            </Button>
                        </Modal.Footer>
                    </Modal>
                )}

                {/* Edit Modal */}
                {selectedChat && (
                    <Modal show={showEditModal} onHide={handleCloseEditModal} size="xl">
                        <Modal.Header closeButton>
                            <Modal.Title>Customized Response</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label>Prompt</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={selectedChat.query}
                                        disabled
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <div className="d-flex align-items-center">
                                        <Form.Label className="mb-0">Customize Response</Form.Label>
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={<Tooltip>Get Bot Response</Tooltip>}
                                        >
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={fetchBotResponse}
                                                disabled={isFetchingBotResponse || !selectedChat.query.trim()}
                                                className="ms-2"
                                                aria-label="Get Bot Response"
                                                ref={botResponseButtonRef}
                                            >
                                                {isFetchingBotResponse ? (
                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                ) : (
                                                    <svg
                                                        width="1.3rem"
                                                        height="1.3rem"
                                                        viewBox="0 0 24 26"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="h-2/3 w-2/3"
                                                    >
                                                        <text x="-9999" y="-9999">ChatGPT</text>
                                                        <path
                                                            d="M9.20509 8.76511V6.50545C9.20509 6.31513 9.27649 6.17234 9.44293 6.0773L13.9861 3.46088C14.6046 3.10413 15.342 2.93769 16.103 2.93769C18.9573 2.93769 20.7651 5.14983 20.7651 7.50454C20.7651 7.67098 20.7651 7.86129 20.7412 8.05161L16.0316 5.2924C15.7462 5.12596 15.4607 5.12596 15.1753 5.2924L9.20509 8.76511ZM19.8135 17.5659V12.1664C19.8135 11.8333 19.6708 11.5955 19.3854 11.429L13.4152 7.95633L15.3656 6.83833C15.5321 6.74328 15.6749 6.74328 15.8413 6.83833L20.3845 9.45474C21.6928 10.216 22.5728 11.8333 22.5728 13.4031C22.5728 15.2108 21.5025 16.8758 19.8135 17.5657V17.5659ZM7.80173 12.8088L5.85130 11.6671C5.68486 11.5721 5.61346 11.4293 5.61346 11.239V6.00613C5.61346 3.46111 7.56389 1.53433 10.2042 1.53433C11.2033 1.53433 12.1307 1.86743 12.9159 2.46202L8.23010 5.17371C7.94475 5.34015 7.80195 5.57798 7.80195 5.91109V12.809L7.80173 12.8088ZM12 15.2349L9.20509 13.6651V10.3351L12 8.76534L14.7947 10.3351V13.6651L12 15.2349ZM13.7958 22.4659C12.7967 22.4659 11.8693 22.1328 11.0841 21.5382L15.7699 18.8265C16.0553 18.6601 16.198 18.4222 16.198 18.0891V11.1912L18.1723 12.3329C18.3388 12.4279 18.4102 12.5707 18.4102 12.761V17.9939C18.4102 20.5389 16.4359 22.4657 13.7958 22.4657V22.4659ZM8.15848 17.1617L3.61528 14.5452C2.30696 13.784 1.42701 12.1667 1.42701 10.5969C1.42701 8.76534 2.52115 7.12414 4.20987 6.43428V11.8574C4.20987 12.1905 4.35266 12.4284 4.63802 12.5948L10.5846 16.0436L8.63415 17.1617C8.46771 17.2567 8.32492 17.2567 8.15848 17.1617ZM7.897 21.0625C5.20919 21.0625 3.23488 19.0407 3.23488 16.5432C3.23488 16.3529 3.25875 16.1626 3.28240 15.9723L7.96817 18.6839C8.25352 18.8504 8.53911 18.8504 8.82446 18.6839L14.7947 15.2351V17.4948C14.7947 17.6851 14.7233 17.8279 14.5568 17.9229L10.0136 20.5393C9.39518 20.8961 8.65780 21.0625 7.89677 21.0625H7.897ZM13.7958 23.8929C16.6739 23.8929 19.0762 21.8474 19.6235 19.1357C22.2874 18.4459 24 15.9484 24 13.4034C24 11.7383 23.2865 10.121 22.002 8.95542C22.121 8.45588 22.1924 7.95633 22.1924 7.45702C22.1924 4.05570 19.4331 1.51045 16.2458 1.51045C15.6037 1.51045 14.9852 1.60549 14.3668 1.81968C13.2963 0.773071 11.8215 0.107086 10.2042 0.107086C7.32606 0.107086 4.92383 2.15256 4.37653 4.86425C1.71260 5.55411 0 8.05161 0 10.5966C0 12.2617 0.713506 13.879 1.99795 15.0446C1.87904 15.5441 1.80764 16.0436 1.80764 16.543C1.80764 19.9443 4.56685 22.4895 7.75421 22.4895C8.39632 22.4895 9.01478 22.3945 9.63324 22.1803C10.7035 23.2269 12.1783 23.8929 13.7958 23.8929Z"
                                                            fill="currentColor"
                                                        />
                                                    </svg>
                                                )}
                                            </Button>
                                        </OverlayTrigger>
                                    </div>
                                    <div className="html-editor-container mt-2">
                                        <JoditEditor
                                            value={editResponse}
                                            config={joditConfig}
                                            tabIndex={1}
                                            onBlur={(newContent) => setEditResponse(newContent)}
                                        />
                                    </div>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Original Response</Form.Label>
                                    <pre style={{
                                        backgroundColor: '#f4f4f4',
                                        padding: '10px',
                                        borderRadius: '5px',
                                        maxHeight: '200px',
                                        overflowY: 'auto',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word'
                                    }}>
                                        <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                                            {selectedChat.response}
                                        </ReactMarkdown>
                                    </pre>
                                </Form.Group>
                            </Form>
                        </Modal.Body>
                        <Modal.Footer className="d-flex justify-content-between">
                            <div>
                                <Button
                                    variant="danger"
                                    onClick={handleShowDeleteModal}
                                    disabled={isDeleting || isSaving || !selectedChat.customized_response}
                                >
                                    {isDeleting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Deleting
                                        </>
                                    ) : (
                                        "Discard Customization"
                                    )}
                                </Button>
                            </div>
                            <div>
                                <Button
                                    variant="secondary"
                                    onClick={handleCloseEditModal}
                                    disabled={isSaving || isDeleting}
                                    style={{ border: '1px solid #ddd', minWidth: '100px' }}
                                    className="me-2"
                                >
                                    Close
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={handleSaveChanges}
                                    disabled={isSaving || isDeleting}
                                    style={{
                                        backgroundColor: '#5a6acf',
                                        borderColor: '#5a6acf',
                                        minWidth: '100px'
                                    }}
                                >
                                    {isSaving ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" ></span>
                                            Saving
                                        </>
                                    ) : (
                                        "Save"
                                    )}
                                </Button>
                            </div>
                        </Modal.Footer>
                    </Modal>
                )}

                {/* Bot Response Modal */}
                {showBotResponseModal && (
                    <Modal
                        show={showBotResponseModal}
                        onHide={handleCloseBotResponseModal}
                        size="lg"
                        dialogClassName="bot-response-modal"
                        backdrop={false}
                        style={{ position: 'fixed' }}
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>Bot Response</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <pre style={{
                                backgroundColor: '#f4f4f4',
                                padding: '10px',
                                borderRadius: '5px',
                                maxHeight: '300px',
                                overflowY: 'auto',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                            }}>
                                <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                                    {botResponse || 'No response available'}
                                </ReactMarkdown>
                            </pre>
                        </Modal.Body>
                        <Modal.Footer className="d-flex justify-content-between align-items-center">
                            <div className="d-flex gap-2">
                                <Button
                                    variant="outline-primary"
                                    onClick={handleCopyResponse}
                                    size="sm"
                                >
                                    Copy
                                </Button>
                                <Button
                                    variant="outline-primary"
                                    onClick={fetchBotResponse}
                                    disabled={isFetchingBotResponse}
                                    size="sm"
                                >
                                    {isFetchingBotResponse ? (
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                    ) : (
                                        "Regenerate"
                                    )}
                                </Button>
                            </div>
                            <Button
                                variant="secondary"
                                onClick={handleCloseBotResponseModal}
                                size="sm"
                                style={{ border: '1px solid #ddd', minWidth: '100px' }}
                            >
                                Close
                            </Button>
                        </Modal.Footer>
                    </Modal>
                )}

                {/* Delete Modal */}
                {selectedChat && (
                    <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
                        <Modal.Header closeButton>
                            <Modal.Title>Confirm Deletion</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            Are you sure you want to discard this customization?
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="secondary"
                                onClick={handleCloseDeleteModal}
                                disabled={isDeleting}
                                style={{ border: '1px solid #ddd', minWidth: '100px' }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="danger"
                                onClick={handleDeleteResponse}
                                disabled={isDeleting}
                                style={{ minWidth: '100px' }}
                            >
                                {isDeleting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Deleting
                                    </>
                                ) : (
                                    "Discard Customization"
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                )}

                {/* Export Modal */}
                <Modal
                    show={showExportModal}
                    onHide={() => setShowExportModal(false)}
                    centered
                    backdrop="static"
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Export Chat History</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            {/* Reaction filter */}
                            <Form.Group className="mb-3">
                                <Form.Label>Reaction</Form.Label>
                                <Form.Select
                                    value={exportReaction}
                                    onChange={(e) => setExportReaction(e.target.value)}
                                >
                                    <option value="all">All Reactions</option>
                                    <option value="1">Positive</option>
                                    <option value="-1">Negative</option>
                                    <option value="0">Neutral</option>
                                </Form.Select>
                            </Form.Group>

                            {/* Date filter – default "Last 1 year" */}
                            <Form.Group className="mb-3">
                                <Form.Label>Date Range</Form.Label>
                                <Form.Select
                                    value={exportDateFilter}
                                    onChange={(e) => setExportDateFilter(e.target.value)}
                                >
                                    <option value="Today">Today</option>
                                    <option value="Last 7 days">Last 7 days</option>
                                    <option value="Last 30 days">Last 30 days</option>
                                    <option value="Last 6 months">Last 6 months</option>
                                    <option value="Last 1 year">Last 1 year</option>
                                </Form.Select>
                            </Form.Group>

                            {/* Format selection – default "excel" */}
                            <Form.Group className="mb-3">
                                <Form.Label>Export Format</Form.Label>
                                <div className="d-flex gap-3">
                                    <Form.Check
                                        type="radio"
                                        id="format-xlsx"
                                        label="Excel "
                                        value="xlsx"
                                        checked={exportFormat === 'xlsx'}
                                        onChange={(e) => setExportFormat(e.target.value)}
                                    />
                                    <Form.Check
                                        type="radio"
                                        id="format-csv"
                                        label="CSV"
                                        value="csv"
                                        checked={exportFormat === 'csv'}
                                        onChange={(e) => setExportFormat(e.target.value)}
                                    />
                                    {/* <Form.Check
                                        type="radio"
                                        id="format-pdf"
                                        label="PDF"
                                        value="pdf"
                                        checked={exportFormat === 'pdf'}
                                        onChange={(e) => setExportFormat(e.target.value)}
                                    /> */}
                                </div>
                            </Form.Group>

                            {/* Optional: small hint about the date range */}
                            <Form.Text className="text-muted">
                                {exportDateFilter === 'all'
                                    ? '⚠️ Exporting all available history may take a moment.'
                                    : `Exporting chats from: ${exportDateFilter}`}
                            </Form.Text>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            onClick={() => setShowExportModal(false)}
                            disabled={isExporting}
                            style={{ border: '1px solid #ddd', minWidth: '100px' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleExport}
                            disabled={isExporting}
                            style={{
                                backgroundColor: '#5a6acf',
                                borderColor: '#5a6acf',
                                minWidth: '100px'
                            }}
                        >
                            {isExporting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Exporting...
                                </>
                            ) : (
                                'Export'
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    );
};

export default ChatHistory;